import ApiClientsService from 'js/services/api/api-clients.js'
import BillingTransactionsService from 'js/services/api/billing/transactions.js'
import BillingProductsService from 'js/services/api/billing/product_validations.js'
import moment from 'moment-timezone'
import _ from 'lodash'
import * as consts from './constants'

const hasValidBillingItems = items => Array.isArray(items) && items.some(item => !_.isEmpty(item))
const transactionId = item => _.get(item, '_id') || item?.id || item?.operation_id

const normalizeFilterArray = value => {
  if (_.isNil(value) || value === '') return []

  return Array.isArray(value) ? value.map(item => String(item)) : [String(value)]
}

const getTscMediaItems = clientData => {
  const mediaItems = clientData?.media || []

  if (!_.isEmpty(mediaItems)) {
    return mediaItems.filter(
      media => String(media.MediaType || media.mediaType) === String(consts.TSC_MEDIA_TYPE)
    )
  }

  return (clientData?.tscs || []).filter(
    media => String(media.MediaType || media.mediaType) === String(consts.TSC_MEDIA_TYPE)
  )
}

const getTravelBaseFilters = rootGetters => ({
  type_array: [consts.VALIDATION_TYPE, consts.PARTIAL_VALIDATION_TYPE],
  from: rootGetters['user/from'],
  until: rootGetters['user/until']
})

const getEffectiveTravelFilters = (rootGetters, extraParams = {}) => {
  const mediaType = rootGetters['user/mediaType']
  const paymentMethod = rootGetters['user/paymentMethod']

  const filters = {
    ...getTravelBaseFilters(rootGetters),
    ...extraParams
  }

  if (_.isEmpty(filters.media_type_array)) {
    if (String(mediaType) === String(consts.QR_ID_MEDIA_TYPE)) {
      filters.media_type_array = []
    } else if (String(mediaType) === String(consts.TSC_MEDIA_TYPE)) {
      filters.media_type_array = [consts.TSC_MEDIA_TYPE]
    }
  }

  if (_.isNil(filters.media_code) || filters.media_code === '') {
    if (
      String(mediaType) !== String(consts.QR_ID_MEDIA_TYPE) &&
      !_.isEmpty(paymentMethod)
    ) {
      filters.media_code = paymentMethod
    }
  }

  return filters
}

const buildTravelContext = rootGetters => ({
  timezone: rootGetters['administrativeArea/administrativeAreaTimeZone'],
  filters: getTravelBaseFilters(rootGetters)
})

const buildTravelFromTransaction = (transaction, TZ) => {
  if (_.isEmpty(transaction) || !transaction.operation_date) return null

  const date = moment(transaction.operation_date).tz(TZ)

  return {
    ...transaction,
    id: transactionId(transaction),
    unix: parseInt(date.format('X')),
    year: date.year(),
    monthIndex: date.format('MM'),
    day: date.format('DD')
  }
}

const matchesTravelFilters = (transaction, filters = {}) => {
  const validTypes = normalizeFilterArray(filters.type_array)
  if (!_.isEmpty(validTypes) && !validTypes.includes(String(transaction.type))) return false

  const validMediaTypes = normalizeFilterArray(filters.media_type_array)
  if (!_.isEmpty(validMediaTypes) && !validMediaTypes.includes(String(transaction.media_type))) {
    return false
  }

  if (!_.isNil(filters.media_code) && filters.media_code !== '') {
    if (String(transaction.media_code) !== String(filters.media_code)) return false
  }

  if (filters.from || filters.until) {
    if (!transaction.operation_date) return false

    const operationDate = moment(transaction.operation_date)
    if (filters.from && operationDate.isBefore(moment(filters.from).startOf('day'))) return false
    if (filters.until && operationDate.isAfter(moment(filters.until).endOf('day'))) return false
  }

  return true
}

const buildTravelsFromTransactions = (transactions, filters, TZ) => transactions
  .filter(item => !_.isEmpty(item))
  .filter(item => matchesTravelFilters(item, filters))
  .map(item => buildTravelFromTransaction(item, TZ))
  .filter(Boolean)
  .sort((a, b) => b.unix - a.unix)

const mergeBillingTravelsWithTransactions = (
  billingTravels,
  transactionsById,
  filters,
  TZ
) => billingTravels
  .filter(item => !_.isEmpty(item))
  .map(trip => {
    const date = moment(trip.operation_date).tz(TZ)
    const transaction = transactionsById[trip.id]

    return {
      ...transaction,
      ...trip,
      unix: parseInt(date.format('X')),
      year: date.year(),
      monthIndex: date.format('MM'),
      day: date.format('DD')
    }
  })
  .filter(item => matchesTravelFilters(item, filters))
  .sort((a, b) => b.unix - a.unix)

export default {
  transactions: {
    state: {
      loadingTravels: false,
      tscs: [],

      transactions: [],
      okProfile: false,
      currentAlias: false,
      selectedTscMediaCode: '',
      modalCapping: false,
      clientData: []
    },
    mutations: {
      setClientData(state, newVal) {
        state.clientData = newVal
      },
      setLoadingTravels(state, newVal) {
        state.loadingTravels = newVal
      },

      setTransactions(state, newVal) {
        state.transactions = newVal
      },
      setTscs(state, newVal) {
        state.tscs = newVal
      },
      setOkProfile(state, newVal) {
        state.okProfile = newVal
      },
      setCurrentAlias(state, newVal) {
        state.currentAlias = newVal
      },
      setSelectedTscMediaCode(state, newVal) {
        state.selectedTscMediaCode = newVal
      },

      openModalCapping(state) {
        state.modalCapping = true
      },
      closeModalCapping(state) {
        state.modalCapping = false
      }
    },
    getters: {
      /** The apis call to client_media returns all of the data we need */
      clientData: (state, getters, _rootState, rootGetters) => rootGetters['user/fullClient'],

      cdProducts(state, getters) {
        return getters.clientData.product_items || []
      },

      cdTransactions(state, getters) {
        return getters.clientData.transactions || []
      },

      cdTransactionsByMediaCode(state, getters) {
        const items = {}
        getters.cdTransactions.forEach(item => {
          if (!_.has(items, item.external_code)) items[item.external_code] = []
          items[item.external_code].push(item)
        })

        return items
      },

      cdTnes(state, getters) {
        return getters.clientData.tnes || []
      },
      /** ------------------------------------------------------------- */

      loadingTravels: state => state.loadingTravels,

      tscs: state => state.tscs,

      transactions: state => state.transactions,
      normalizedTransactions: state => {
        const source = !_.isEmpty(state.transactions) ?
          state.transactions :
          (state.clientData?.transactions || [])

        const deduped = []
        const seen = new Set()

        source.forEach(item => {
          const key = _.get(item, '_id') || item?.id || item?.operation_id
          if (!key || seen.has(key)) return
          seen.add(key)
          deduped.push(item)
        })

        return deduped
      },
      normalizedTransactionsMap: (state, getters) => getters.normalizedTransactions.reduce(
        (output, item) => {
          const key = transactionId(item)
          if (key) output[key] = item

          return output
        },
        {}
      ),
      derivedTravels: (state, getters, rootState, rootGetters) => (extraParams = {}) => {
        const filters = getEffectiveTravelFilters(rootGetters, extraParams)
        const {timezone} = buildTravelContext(rootGetters)

        return buildTravelsFromTransactions(getters.normalizedTransactions, filters, timezone)
      },
      accountTravels: (state, getters, rootState, rootGetters) => {
        const {timezone, filters} = buildTravelContext(rootGetters)

        return buildTravelsFromTransactions(getters.normalizedTransactions, {
          ...filters
        }, timezone)
      },
      tscTravels: (state, getters, rootState, rootGetters) => {
        const {timezone, filters} = buildTravelContext(rootGetters)

        return buildTravelsFromTransactions(getters.normalizedTransactions, {
          ...filters,
          media_type_array: [consts.TSC_MEDIA_TYPE],
          media_code: state.selectedTscMediaCode
        }, timezone)
      },
      transactionById: (state, getters) => journeyId => {
        if (!journeyId) return null

        return getters.normalizedTransactionsMap[String(journeyId)] || null
      },
      /**
       *
       * @param {String} rootGetters
       * @returns
       */
      getPaymentTypeText: (state, getters, rootState, rootGetters) => code => {
        const paymentTypes = rootGetters['user/mediaTypes']
        if (_.isEmpty(paymentTypes)) return null
        const paymentType = paymentTypes.find(mt => String(mt.code) === String(code))

        return (paymentType?.name || '').split(' ')[0]
      },

      paymentTypeIcon() {
        return media_type => {
          if (consts.QR_TYPES.includes(Number(media_type))) return 'qr-mini'
          if (consts.TOKEN_TYPES.includes(Number(media_type))) return 'tne'

          return 'qr-mini'
        }
      },

      /**
       * Attempts to find the product related to a transaction
       * @param {*} idTransaction
       * @returns {Object|null}
       */
      mapTransactionToProdcuct(state, getters, rootState, rootGetters) {
        return idTransaction => {
          const products = rootGetters.products
          if (_.isEmpty(products)) return null

          return products.find(item => item.id === idTransaction)
        }
      },

      okProfile: state => state.okProfile,
      currentAlias: state => state.currentAlias,
      selectedTscMediaCode: state => state.selectedTscMediaCode,
      totalElements: (state, getters) => getters.normalizedTransactions.length,

      modalCapping: state => state.modalCapping
    },
    actions: {
      checkIfCanRequestTravels({rootGetters, commit, dispatch}) {
        // Tengo cuenta en el municipio actual
        if (rootGetters.currentClientAccount) {
          commit('setLoadingTravels', true)
          const params = {
            administrative_area_id:
              rootGetters.currentClientAccount.administrative_area_id,
            rut: rootGetters.currentClientAccount.rut
          }
          ApiClientsService.clientMediaInfo(params)
            .then(
              /** Success */
              _response => {
                const data = rootGetters['user/fullClient'] || {}
                commit('setClientData', data)
                commit('setTscs', getTscMediaItems(data))
                // Hay medios en Everilion
                commit('setOkProfile', true)

                dispatch('getProducts')
                dispatch('getTravels')
                commit('setTransactions', data.transactions || [])

                // No hay medios en Everilion
              },
              /** Error */
              error => {
                console.log('error ajax', error)
              }
            )
            .finally(() => {
              commit('setLoadingTravels', false)
            })
        }
      },

      getTravels({commit, getters, rootGetters}, extraParams = {}) {
        /** Execute the new process in order to avoid refactoring */
        const params = {
          ...extraParams,
          administrative_area_id:
            rootGetters.currentClientAccount.administrative_area_id,
          // product_item_code: rootGetters['mediaType'],
          type_array: [consts.VALIDATION_TYPE, consts.PARTIAL_VALIDATION_TYPE],
          order_by: 'operation_date',
          order_dir: 'desc',
          until: rootGetters.until,
          from: rootGetters.from,
          per: 500,
          template: 'client_travel_v2'
        }

        const TZ = rootGetters['administrativeArea/administrativeAreaTimeZone']

        return BillingTransactionsService.index(params)
          /** Success */
          .then(response => {
            /** Modify the response before committing */
            const derivedTravels = getters.derivedTravels(params)
            let output = derivedTravels

            if (hasValidBillingItems(response)) {
              const effectiveFilters = getEffectiveTravelFilters(rootGetters, params)

              output = mergeBillingTravelsWithTransactions(
                response,
                getters.normalizedTransactionsMap,
                effectiveFilters,
                TZ
              )
            }

            commit('setTravels', output)
          })
          /** Failure */
          .catch(error => {
            console.log('ajax error getting journeys', error)
          })
      },

      getProducts({rootGetters, commit}, extraParams = {}) {
        const params = {
          ...extraParams,
          administrative_area_id:
            rootGetters.currentClientAccount.administrative_area_id,
          rut: rootGetters.currentClientAccount.rut,
          order_by: 'operation_date',
          order_dir: 'desc',
          until: rootGetters.until,
          from: rootGetters.from,
          template: 'client_travel_v2'
        }

        return (
          BillingProductsService.index(params)
            /** Success */
            .then(response => {
              commit(
                'setProducts',
                response.filter(media => media.type === consts.PRODUCT_TYPE)
              )
            })
            /** Failure */
            .catch(error => {
              console.log('ajax error getting purchases', error)
            })
        )
      },

      openModalCapping({commit}) {
        commit('openModalCapping')
      },

      closeModalCapping({commit}) {
        commit('closeModalCapping')
      }
    }
  }
}
